import _ from 'lodash'
import { toast } from 'react-toastify'

import type { useCatalogFiltersStore } from 'features/catalog/store'
import { useCatalogQueryStore } from 'features/catalog/yogiql-store'
import type { SelectedFilterOption } from 'features/charts-v2/types'
import type { ChartPanel } from 'features/charts-v2/types/filter-hash/v2'
import type { Condition } from 'features/charts-v2/types/yogiql'
import { warnYogiQLFallback } from 'features/charts-v2/utils'
import {
  encodeDate,
  encodeEachFilter,
  encodeFilters,
  encodeSearch,
  type FeedbackQueryCodec,
  inSlot,
  isDateCondition,
  isSearchCondition,
  readQuery,
  replaceWhere,
  type SearchFields,
  slotOf,
  withSavedQuery,
} from 'features/feedback/feedback-query'
import type { FilterOption, YogiQLFilterPreset } from 'features/filters/types'
import { trackSavedFilterLoaded } from 'trackers/shared-events'
import type { Surface } from 'types/project'

// ---------------------------------------------------------------------------
// The flagged sidebar's store actions. `buildFilterControlProps` hands out v2
// edits (a field's selection, panel field updates); each one is applied to
// the panel's YogiQL query by replacing only the conditions it touches.
// Mirrors keywords' `keywords-yogiql-sidebar-handlers.ts`, minus the panel
// switcher and cross-panel sync (catalog has exactly one panel).
// ---------------------------------------------------------------------------

type CatalogFiltersActions = ReturnType<typeof useCatalogFiltersStore.getState>

export type CatalogQueryActions = Pick<
  CatalogFiltersActions,
  | 'updateFilterSelection'
  | 'updatePanel'
  | 'enableFilter'
  | 'disableFilter'
  | 'dropGhost'
>

const panelFilters = (panelId: string): Condition[] | undefined =>
  useCatalogQueryStore.getState().panels.find((p) => p.id === panelId)?.query
    .filters

// Writes a panel's conditions, dropping ghosts whose field is live again.
function writeFilters(
  panelId: string,
  filters: Condition[],
  options: FilterOption[],
): void {
  const store = useCatalogQueryStore.getState()
  store.setFilters(panelId, filters)
  const ghosts = store.ghosts[panelId]
  if (!ghosts) return
  const live = new Set(filters.map((c) => slotOf(c, options)))
  const kept = _.omit(ghosts, [...live])
  if (_.size(kept) !== _.size(ghosts)) store.setGhosts(panelId, kept)
}

/** The saved-filter picker's apply under the flag: the preset's conditions,
 *  as the YogiQL list names them, replace the panel's. */
export function buildSavedQueryApplyHandler(
  panelId: string,
  options: FilterOption[],
  surface: Surface,
): (preset: YogiQLFilterPreset) => void {
  return (preset) => {
    const filters = panelFilters(panelId)
    if (!preset.query) {
      warnYogiQLFallback(
        `saved filter "${preset.name}" not applied: ${preset.unconvertible.join('; ')}`,
      )
      toast.error(`"${preset.name}" uses a filter this page cannot apply.`)
      return
    }
    if (!filters) return
    trackSavedFilterLoaded({
      surface,
      filter_name: preset.name,
      criteria_count: preset.query.filters.length,
    })
    writeFilters(
      panelId,
      withSavedQuery(filters, preset.query.filters),
      options,
    )
  }
}

/** Sets a panel's date window; the table toolbar's date control writes through this. */
export function writeDateRange(
  panelId: string,
  dateRange: ChartPanel['dateRange'],
): void {
  const filters = panelFilters(panelId)
  if (!filters) return
  useCatalogQueryStore
    .getState()
    .setFilters(
      panelId,
      replaceWhere(filters, isDateCondition, encodeDate(dateRange)),
    )
}

/** Drops a panel's filter chips, keeping its date and search, as the v2
 *  `updatePanel(id, { filters: [] })` does. */
export function clearFilters(panelId: string): void {
  const filters = panelFilters(panelId)
  if (!filters) return
  useCatalogQueryStore.getState().setFilters(
    panelId,
    filters.filter((c) => isDateCondition(c) || isSearchCondition(c)),
  )
}

function updateSearch(
  panelId: string,
  filters: Condition[],
  updates: Partial<ChartPanel>,
): Condition[] {
  const current = readQuery(filters.filter(isSearchCondition), [])
  const search: SearchFields = {
    searchTerms: updates.searchTerms ?? current.searchTerms,
    searchCriteria: updates.searchCriteria ?? current.searchCriteria,
    smartSearchEnabled:
      updates.smartSearchEnabled ?? current.smartSearchEnabled,
  }
  useCatalogQueryStore.getState().setShownSearch(panelId, search)
  return replaceWhere(filters, isSearchCondition, encodeSearch(search))
}

const SEARCH_KEYS = ['searchTerms', 'searchCriteria', 'smartSearchEnabled']

/** The v2 actions `buildFilterControlProps` takes, writing YogiQL. */
export function buildCatalogQueryActions(
  codec: FeedbackQueryCodec,
): CatalogQueryActions {
  const { options } = codec
  return {
    updateFilterSelection: (panelId, field, operator, values, range) => {
      const filters = panelFilters(panelId)
      if (!filters) return
      const isDelete =
        values.length === 0 &&
        range?.numericValue === undefined &&
        range?.min === undefined &&
        range?.max === undefined &&
        range?.valueType === undefined
      const next = isDelete
        ? []
        : encodeFilters(
            [{ field, operator, values, ...range } as SelectedFilterOption],
            codec,
          )
      if (typeof next === 'string') {
        warnYogiQLFallback(`catalog filter edit not applied: ${next}`)
        return
      }
      writeFilters(
        panelId,
        replaceWhere(filters, inSlot(field, options), next),
        options,
      )
    },

    updatePanel: (panelId, updates) => {
      let filters = panelFilters(panelId)
      if (!filters) return
      if (updates.filters) {
        const kept = filters.filter(
          (c) => isDateCondition(c) || isSearchCondition(c),
        )
        filters = [...encodeEachFilter(updates.filters, codec), ...kept]
      }
      if (SEARCH_KEYS.some((key) => key in updates)) {
        filters = updateSearch(panelId, filters, updates)
      }
      if (updates.dateRange) {
        filters = replaceWhere(
          filters,
          isDateCondition,
          encodeDate(updates.dateRange),
        )
      }
      writeFilters(panelId, filters, options)
    },

    disableFilter: (panelId, field) => {
      const filters = panelFilters(panelId)
      if (!filters) return
      const matches = inSlot(field, options)
      const ghosted = filters.filter(matches)
      if (!ghosted.length) return
      const store = useCatalogQueryStore.getState()
      store.setFilters(
        panelId,
        filters.filter((c) => !matches(c)),
      )
      store.setGhosts(panelId, {
        ...store.ghosts[panelId],
        [field]: ghosted,
      })
    },

    enableFilter: (panelId, field) => {
      const store = useCatalogQueryStore.getState()
      const ghosted = store.ghosts[panelId]?.[field]
      const filters = panelFilters(panelId)
      if (!ghosted || !filters) return
      store.setGhosts(panelId, _.omit(store.ghosts[panelId], field))
      store.setFilters(
        panelId,
        replaceWhere(filters, inSlot(field, options), ghosted),
      )
    },

    dropGhost: (panelId, field) => {
      const store = useCatalogQueryStore.getState()
      store.setGhosts(panelId, _.omit(store.ghosts[panelId], field))
    },
  }
}

/** A panel's ghosted conditions, read as the v2 filters the cards show. */
export const readGhosts = (
  ghosts: Record<string, Condition[]> | undefined,
  options: FilterOption[],
): SelectedFilterOption[] =>
  Object.values(ghosts ?? {}).flatMap(
    (conditions) => readQuery(conditions, options).filters,
  )

/** The shown search cards while they still encode to the query's search. */
export function withShownSearch(
  panel: ChartPanel,
  filters: Condition[],
  shown: SearchFields | undefined,
): ChartPanel {
  if (!shown) return panel
  const held = filters.filter(isSearchCondition)
  return _.isEqual(encodeSearch(shown), held) ? { ...panel, ...shown } : panel
}

/** The table toolbar's `updatePanel` under the flag; it only sets the date. */
export function updateQueryPanelDate(
  panelId: string,
  updates: Partial<ChartPanel>,
): void {
  if (updates.dateRange) writeDateRange(panelId, updates.dateRange)
}
